// Shared domain types. Hand-written to mirror CRM3 conventions.
// Snake_case fields match Postgres rows; camelCase types are domain-level.

export type Role = 'admin' | 'supervisor' | 'studioleiter' | 'servicekraft' | 'callcenter_agent' | 'verkaeufer';

// Roles allowed to see all calls / manage telephony state org-wide.
// Mirrors callcenter_telefonie.is_privileged() in the DB.
export const PRIVILEGED_ROLES: ReadonlyArray<Role> = ['admin', 'supervisor'];

/** So steht die Rolle in der Oberfläche (Ansichts-Vorschau, Meldung über „Feedback"). */
export const ROLE_LABEL: Record<Role, string> = {
  admin: 'Admin',
  supervisor: 'Supervisor',
  studioleiter: 'Studioleiter',
  servicekraft: 'Servicekraft',
  callcenter_agent: 'Callcenter-Agent',
  verkaeufer: 'Verkäufer',
};

export interface SharedUser {
  id: string;                                  // auth.users.id
  email: string;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
  is_active?: boolean;
  created_at?: string;
  last_login?: string | null;
}

export type CallDirection = 'outbound' | 'inbound';
export type CallStatus =
  | 'queued'
  | 'ringing'
  | 'in-progress'
  | 'completed'
  | 'no-answer'
  | 'busy'
  | 'failed'
  | 'canceled';

export interface CallLogRow {
  id: string;
  legacy_call_id: number | null;
  customer_id: string | null;
  legacy_customer_id: number | null;
  agent_id: string | null;
  legacy_user_id: number | null;
  direction: CallDirection;
  call_sid: string | null;
  call_status: CallStatus | string | null;
  answered: boolean;
  duration_seconds: number;
  from_number: string | null;
  to_number: string | null;
  recording_url: string | null;
  comment: string | null;
  metadata: Record<string, unknown>;
  call_timestamp: string;
}

export interface VoicemailRow {
  id: string;
  call_sid: string;
  customer_id: string | null;
  agent_id: string | null;
  from_number: string;
  to_number: string;
  duration_seconds: number | null;
  twilio_recording_url: string;
  storage_url: string | null;
  storage_status: string;
  // Übernahme aus dem Altsystem? Dann liegt die Aufnahme im alten
  // Telefonie-Konto und ist erst nach der Rettung ins Archiv abspielbar.
  legacy_id: number | null;
  deleted_at: string | null;
  crm_status: 'new' | 'in_progress' | 'done' | 'archived';
  notes: string | null;
  created_at: string;
  // Aus v_voicemails: Name/Standort des zugeordneten Kunden (null, wenn die
  // Nummer keinem Kunden zugeordnet ist).
  customer_name: string | null;
  school_name: string | null;
}

export interface DialableLead {
  customer_id: string;
  full_name: string;
  phone_number: string;
  status: string;
  not_reached_count: number;
  school_id: string | null;
  school_name: string | null;
  source: 'lead' | 'probetraining' | 'member';
  last_contact_at: string | null;
  entered_at?: string | null; // nur aus v_dialable_leads; bei Direktwahl/Walk-in nicht gesetzt
  // Alters-Infos (nur aus v_dialable_leads): echtes Alter aus dem Profil bzw.
  // Altersspanne des angefragten Kurses; age_from/age_to sind die vom Server
  // normalisierten Filtergrenzen (echtes Alter gewinnt, offene Enden 0/120).
  age?: number | null;
  req_age_min?: number | null;
  req_age_max?: number | null;
  age_from?: number | null;
  age_to?: number | null;
  // Kind/Erwachsener (nur aus v_dialable_leads). `is_for_child` ist die rohe
  // Angabe aus der jüngsten Formular-Anfrage, `person_kind` die daraus (bzw.
  // aus Alter/Kurs) abgeleitete Einstufung — null, wenn nichts bekannt ist.
  is_for_child?: boolean | null;
  person_kind?: string | null;
  // Ad-hoc direct dial (walk-in / number not in the system): no real customer,
  // so the dialer must not send a CustomerId to Twilio.
  ad_hoc?: boolean;
}

export interface ModulePermissionRow {
  module_key: string;
  can_view: boolean;
  can_edit: boolean;
  can_delete: boolean;
}

export interface UserAdminRow {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
  is_active: boolean;
  last_login: string | null;
  created_at: string;
  role: Role;
}

export interface PhoneNumberRow {
  id: string;
  phone_number: string;
  label: string | null;
  school_id: string | null;
  is_default: boolean;
  active: boolean;
}

export interface UserPhoneAssignment {
  user_id: string;
  phone_number_id: string;
}

export interface PhoneBlacklistRow {
  phone_number: string;
  reason: string | null;
  created_by: string | null;
  created_at: string;
}

export interface MessageTemplateRow {
  id: string;
  template_name: string;
  channel: 'sms' | 'whatsapp' | 'email' | string;
  subject: string | null;
  body: string;
  is_active: boolean;
  is_html: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface CallcenterCustomerRow {
  customer_id: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  email: string | null;
  status: string | null;
  school_id: string | null;
  school_name: string | null;
  call_attempts: number;
  last_call_at: string | null;
  is_blacklisted: boolean;
  created_at: string;
  updated_at: string;
  // Aus v_callcenter_customers (security_invoker=false) — bewusst über die
  // View statt der Rohtabelle crm.customers gelesen, da diese RLS aktiviert
  // aber keine Policies hat und für normale Rollen sonst 0 Zeilen liefert.
  mobile: string | null;
  notes: string | null;
  birth_date: string | null;
  lifecycle_stage: string | null;
  age_override: number | null;
  legacy_id: number | null;
  registration_date: string | null;
}

export interface CallWithNamesRow {
  id: string;
  customer_id: string | null;
  agent_id: string | null;
  direction: CallDirection;
  call_status: string | null;
  answered: boolean;
  duration_seconds: number;
  from_number: string | null;
  to_number: string | null;
  call_timestamp: string;
  comment: string | null;
  recording_url: string | null;
  protocol: CallProtocolState | null;
  customer_name: string | null;
  agent_name: string | null;
  agent_email: string | null;
  // Was ist am Telefon passiert (erreicht / mailbox / niemand / besetzt / …)?
  // Optional, da die Spalte erst mit der Migration existiert.
  outcome?: string | null;
}

// Wer hat mit diesem Kunden telefoniert, wie oft, wann zuletzt.
export interface CustomerHandlerRow {
  agent_id: string | null;
  agent_name: string | null;
  anrufe: number;
  erreicht: number;
  erster: string | null;
  letzter: string | null;
}

// Geschwister/Duplikate eines Kunden (RPC customer_siblings, Migration 1087).
export interface CustomerSiblingRow {
  sibling_customer_id: string;
  first_name: string | null;
  last_name: string | null;
  birth_date: string | null;
  age_override: number | null;
  status: string | null;
  lifecycle_stage: string | null;
  school_name: string | null;
}

export interface LeaderboardRow {
  agent_id: string;
  full_name: string | null;
  email: string;
  total_calls: number;
  answered_calls: number;
  outbound: number;
  inbound: number;
  total_duration_s: number;
  last_call_at: string | null;
}

// Callcenter-Statistik: eine Zeile je Kollegin für den gewählten Zeitraum.
// Probetermine zählen am Tag der Vereinbarung, die Ausgänge summieren sich
// deshalb immer auf `termine_vereinbart` (Migration 1088).
export interface CallcenterStatistikRow {
  agent_id: string;
  agent_name: string;
  anrufversuche: number;
  telefonate: number;
  gespraechsdauer_s: number;
  termine_vereinbart: number;
  termine_erschienen: number;
  termine_nicht_erschienen: number;
  termine_abgesagt: number;
  termine_verlegt: number;
  termine_offen: number;
  nicht_vereinbart: number;
  /** Erreichte Gespräche, für die die Telefonanlage keine Dauer gemeldet hat. */
  telefonate_ohne_dauer: number;
}

export type GrundArt = 'kein_termin' | 'absage' | 'verlegung';

// Eine Zeile des Verlaufs: ein Zeitpunkt, eine Person. Der Zeitpunkt kommt
// als Text in Berliner Ortszeit („2026-09-18" bzw. „2026-09-18 14"), damit
// bei der Zuordnung keine Zeitzone dazwischenkommt (Migration 1090).
export interface CallcenterVerlaufRow {
  zeitpunkt: string;
  agent_id: string;
  anrufversuche: number;
  telefonate: number;
  termine_vereinbart: number;
}

export interface CallcenterGrundRow {
  art: GrundArt;
  grund: string;
  anzahl: number;
}

export interface SchoolRow {
  id: string;
  name: string;
}

export interface LegacyUserRow {
  legacy_user_id: number;
  username: string;
  role: string | null;
  mapped_user_id: string | null;
  call_count: number;
}

export interface AuditLogRow {
  id: string;
  actor_id: string | null;
  actor_email: string | null;
  action: 'INSERT' | 'UPDATE' | 'DELETE';
  entity_schema: string;
  entity_table: string;
  entity_id: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  created_at: string;
}

export interface CallbackRow {
  id: string;
  customer_id: string;
  agent_id: string | null;
  due_at: string;
  note: string | null;
  status: 'pending' | 'done' | 'cancelled';
  created_at: string;
  completed_at: string | null;
  customer_name: string | null;
  customer_phone: string | null;
  agent_name: string | null;
  // Wer hat die Wiedervorlage angelegt / zugewiesen? Zeigt in der Liste
  // „von Sharina zugewiesen", wenn jemand anderes zuständig ist als der
  // Ersteller. Optional, da created_by_name erst mit der Migration existiert.
  created_by?: string | null;
  created_by_name?: string | null;
  school_id: string | null;
  school_name: string | null;
  // 'agent' = Mitarbeiter-Rückruf (Standard), 'ai' = KI-Anruf (Fonio).
  // Optional, da die Spalte erst mit der Migration existiert.
  kind?: 'agent' | 'ai' | string | null;
  // Herkunft: 'manual' = von einer Person gesetzt (Standard),
  // 'system' = automatisch erzeugter Reminder (z. B. Probetraining-Erinnerung).
  // Optional, da die Spalte erst mit der Migration existiert.
  source?: 'manual' | 'system' | string | null;
  // Letzter ausgehender Anruf an diese Person (inkl. Alt-Anrufe) — zeigt in den
  // Wiedervorlagen, ob/wann schon angerufen wurde. Optional (Migration).
  last_call_at?: string | null;
  last_call_reached?: boolean | null;
  // Ergebnis des letzten Anrufs (erreicht / mailbox / niemand / …) und wer ihn
  // gemacht hat — „Mailbox" ist eine andere Entscheidung als „niemand dran".
  last_call_outcome?: string | null;
  last_call_agent?: string | null;
  // Dritter Status neben Erledigt/Abgebrochen: „In Bearbeitung" — die Person
  // wurde versucht, aber nicht direkt erreicht, der Eintrag bleibt trotzdem in
  // der Übersicht (anders als Häkchen/Kreuz). Optional, da die Spalte erst mit
  // der Migration existiert.
  in_progress?: boolean | null;
}

export interface AgentSettings {
  user_id: string;
  pause_between_calls_s: number;
  auto_voicemail_skip: boolean;
  retry_count: number;
  auto_advance: boolean;
  test_mode_enabled: boolean;
  test_mode_override_number: string | null;
  protocol_template_id: string | null;
  prefs: Record<string, unknown>;
  updated_at: string;
}

export type ProtocolMode = 'outcome' | 'flow';

export interface ProtocolStep {
  id: string;
  label: string;
  hint?: string;
  script?: string;
  required?: boolean;
  notes_enabled?: boolean;
}

export interface ProtocolSideStep {
  id: string;
  label: string;
  answer: string;
}

export interface ProtocolTemplate {
  id: string;
  name: string;
  description: string | null;
  mode: ProtocolMode;
  steps: ProtocolStep[];
  side_steps: ProtocolSideStep[];
  is_default: boolean;
  updated_at?: string;
}

// Interesse/Sportart eines Kunden (callcenter_telefonie.customer_interest):
// vom Team gesetzt > Angabe aus dem Website-Formular. Kein Altsystem-Fallback
// mehr — die alte Datenbank ist seit 27.07.2026 abgeschaltet.
// `interest` ist der anzuzeigende Wert, `source` woher er stammt.
export interface CustomerInterest {
  interest: string | null;
  source: 'manual' | 'form' | null;
  form_interest: string | null;
}

// Formular-Anfrage ohne gewählten Kurs („Ich bin mir unsicher") — steht
// ausdrücklich so in der Akte, statt eine Sportart zu raten.
export const INTEREST_UNKNOWN = 'weiß ich nicht';

// Persisted in call_log.protocol JSONB
export interface CallProtocolState {
  template_id: string | null;
  template_name?: string;
  mode: ProtocolMode;
  checked: Record<string, boolean>;
  step_notes: Record<string, string>;
  side_steps_used: string[];
  free_notes: string;
  disposition: string | null;
  saved_at?: string;
}

export interface ClassBookingRow {
  customer_id: string;
  full_name: string;
  phone: string | null;
  email: string | null;
  status: string | null;
  booking_status: string | null;
  attended: boolean | null;
}

// Gebuchtes Probetraining, dessen Kurs im CRM-Stundenplan inzwischen
// abgesagt oder verschoben wurde (RPC affected_trial_bookings, Migration 1073).
export interface AffectedBookingRow {
  booking_id: string;
  customer_id: string;
  full_name: string;
  phone: string | null;
  school: string | null;
  sport: string;
  booking_date: string;
  start_time: string;
  canceled: boolean;
  moved: boolean;
  /** Neuer Termin, falls der Kurs auf einen anderen Tag gelegt wurde. */
  new_date: string | null;
  new_start: string | null;
  change_note: string | null;
  changed_by: string | null;
}

export interface BackOnTrackCandidateRow {
  customer_id: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  email: string | null;
  member_since: string | null;
  school_id: string | null;
  school_name: string | null;
  last_call_at: string | null;
  days_since: number;
}
